import type {SyntheticEvent} from "react";
import {useTranslation} from "components/translations/useTranslation";
import type {CommentsProfileProps} from "types/taskDetailsComments";
import commentsDefaultUserImage from "assets/comments-default-user.png";
import approved from "assets/24_OK.svg";
import autoApproved from "assets/24_autoapprove.svg";
import awaitingReview from "assets/24_awaiting_review.svg";
import canceled from "assets/24_cancelled1.svg";
import forwarded from "assets/24_forwarded_task.svg";
import rejected from "assets/24_rejected_document1.svg";
import reviewed from "assets/24_document_reviewed.svg";
import reassigned from "assets/24_reassign.svg";
import systemTask from "assets/24_system_task.svg";
import accountView from "assets/accountView.png";
import business from "assets/business_x64.png";
import businessNXT from "assets/business_NXT.svg";
import control from "assets/control_x64.png";
import documentCenter from "assets/documentCenter_x64.png";
import calendar from "assets/visma_calendar.svg";

const SERVICES_IMAGES_URL = "https://static-odp.visma.net/commoncomponents/vismaSkin/images/services";
const FALLBACK_IMAGE = `${SERVICES_IMAGES_URL}/default/medium.png`;

// the icon of the action the comment was given with, by the event type of the comment
const EVENT_ICONS: Record<number, string> = {
    0: systemTask,
    1: approved,
    2: awaitingReview,
    3: rejected,
    4: canceled,
    5: forwarded,
    9: autoApproved,
    22: reviewed,
    55: reassigned,
};

// the comment event types that get no action icon
const EVENT_TYPE_WITHOUT_ICON = 7;

const APPLICATION_IMAGES: Record<string, string> = {
    absence: calendar,
    documentcenter: documentCenter,
    business: business,
    business_cloud: businessNXT,
    control: control,
    accountview: accountView,
};

const getApplicationImage = (externalApplicationName: string): string => {
    const applicationId = externalApplicationName.toLowerCase().replace(/visma(\.net)? /, "").replace(/ /g, "_");
    return APPLICATION_IMAGES[applicationId] ?? `${SERVICES_IMAGES_URL}/${applicationId}/medium.png`;
};

const setFallbackImage = (event: SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = FALLBACK_IMAGE;
};

/**
 * Who wrote a comment: the user's profile picture, or the logo of the application the comment came
 * from, with the icon of the action it was given with in its corner.
 */
export const CommentsProfile = ({connectUserId, userName, externalApplicationName, eventType}: CommentsProfileProps) => {
    const {translate} = useTranslation();

    const profilePictureBaseUrl = window.ENVIRONMENT?.PROFILE_PICTURE_BASE_URL;
    const profilePicture = connectUserId && profilePictureBaseUrl
        ? `${profilePictureBaseUrl}${connectUserId}?type=initials`
        : commentsDefaultUserImage;
    const picture = externalApplicationName ? getApplicationImage(externalApplicationName) : profilePicture;
    const eventIcon = eventType && eventType !== EVENT_TYPE_WITHOUT_ICON ? EVENT_ICONS[eventType] : undefined;

    return (
        <div className="comments-module__profile">
            <div className="ga-avatar">
                <img src={picture}
                     className="comments-module__picture"
                     alt={translate("taskDetail.profilePicture", userName)}
                     onError={setFallbackImage}/>
            </div>
            {eventIcon && <img className="comments-module__event-icon" src={eventIcon} alt="icon"/>}
        </div>
    );
};

export default CommentsProfile;
